import { useCallback, useState } from 'react'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import useSentry from 'Pods/Shared/Hooks/useSentry'

export const RETRY_STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUBMITTED: 'submitted',
}

function useSubscription({
  subscriptionData,
  messages,
  onRestoreSuccess,
  onRestoreError,
  onRedirectToPaymentForm,
  onCancelPlanSwitchSuccess,
  onPauseError,
  onResumeError,
}) {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [retryState, setRetryState] = useState(RETRY_STATE.IDLE)
  const [isPauseLoading, setIsPauseLoading] = useState(false)
  const [isResumeLoading, setIsResumeLoading] = useState(false)
  const { postData, isLoading } = useApiPost()
  const { deleteData, isLoading: isCancelPlanSwitchLoading } = useApiDelete()
  const { showSuccess, showError } = useNotifications()
  const { captureException } = useSentry()

  const handleOpenCancelModal = useCallback(() => {
    setIsCancelModalOpen(true)
  }, [])

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false)
  }, [])

  const handleCancelSubscription = useCallback(
    (reason) => {
      if (!subscriptionData?.id) return

      postData({
        path: '/api/subscriptions/cancel_subscription',
        data: { id: subscriptionData.id, reason },
        onSuccess: () => {
          window.location.reload()
        },
        onError: (error) => {
          const errorMessage =
            error.response?.data?.error_message || messages.cancelError
          showError(errorMessage)
          captureException(error, {
            tags: { context: 'cancel_subscription' },
            extra: { subscriptionId: subscriptionData.id, reason },
          })
        },
      })
    },
    [subscriptionData, messages, postData, showError, captureException]
  )

  const handleRestoreSubscription = useCallback(() => {
    if (!subscriptionData?.id) return

    postData({
      path: '/api/subscriptions/restore_subscription',
      data: { id: subscriptionData.id },
      onSuccess: (response) => {
        if (response?.data?.redirectTo) {
          onRedirectToPaymentForm?.(response.data.redirectTo)
          return
        }

        const renewalScheduled = Boolean(response?.data?.renewal_scheduled)

        showSuccess(messages.restoreSuccess)
        onRestoreSuccess?.({ renewalScheduled })
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error_message || messages.restoreError
        showError(errorMessage)
        captureException(error, {
          tags: { context: 'restore_subscription' },
          extra: { subscriptionId: subscriptionData.id },
        })
        onRestoreError?.(errorMessage)
      },
    })
  }, [
    subscriptionData,
    messages,
    onRestoreSuccess,
    onRestoreError,
    onRedirectToPaymentForm,
    postData,
    showSuccess,
    showError,
    captureException,
  ])

  const handleRetryPayment = useCallback(() => {
    if (!subscriptionData?.id) return

    setRetryState(RETRY_STATE.LOADING)

    postData({
      path: '/profile/subscription/retry-payment',
      onSuccess: () => {
        setRetryState(RETRY_STATE.SUBMITTED)
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error_message || messages.retryError
        showError(errorMessage)
        setRetryState(RETRY_STATE.IDLE)
        captureException(error, {
          tags: { context: 'retry_payment' },
          extra: { subscriptionId: subscriptionData.id },
        })
      },
    })
  }, [subscriptionData, messages, postData, showError, captureException])

  // Resolves to true on success so callers can chain UI steps (confirmation modal, reload).
  // The backend never returns pause dates here — Solidgate webhooks are authoritative, so
  // callers must refetch instead of updating lifecycle state locally.
  const handlePauseSubscription = useCallback(() => {
    if (!subscriptionData?.id || isPauseLoading) return Promise.resolve(false)

    setIsPauseLoading(true)

    return new Promise((resolve) => {
      postData({
        path: '/api/subscriptions/pause_subscription',
        data: { id: subscriptionData.id },
        onSuccess: () => {
          setIsPauseLoading(false)
          resolve(true)
        },
        onError: (error) => {
          const errorCode = error.response?.data?.error
          setIsPauseLoading(false)
          showError(messages.pauseError(errorCode))
          captureException(error, {
            tags: { context: 'pause_subscription' },
            extra: { subscriptionId: subscriptionData.id, errorCode },
          })
          onPauseError?.(errorCode)
          resolve(false)
        },
      })
    })
  }, [subscriptionData, isPauseLoading, messages, postData, showError, captureException, onPauseError])

  const handleResumeSubscription = useCallback(() => {
    if (!subscriptionData?.id || isResumeLoading) return Promise.resolve(false)

    setIsResumeLoading(true)

    return new Promise((resolve) => {
      postData({
        path: '/api/subscriptions/resume_subscription',
        data: { id: subscriptionData.id },
        onSuccess: () => {
          setIsResumeLoading(false)
          resolve(true)
        },
        onError: (error) => {
          const errorCode = error.response?.data?.error
          setIsResumeLoading(false)
          showError(messages.resumeError(errorCode))
          captureException(error, {
            tags: { context: 'resume_subscription' },
            extra: { subscriptionId: subscriptionData.id, errorCode },
          })
          onResumeError?.(errorCode)
          resolve(false)
        },
      })
    })
  }, [subscriptionData, isResumeLoading, messages, postData, showError, captureException, onResumeError])

  const handleCancelPlanSwitch = useCallback(() => {
    deleteData({
      path: '/profile/subscription/plan_switch',
      onSuccess: () => {
        showSuccess(messages.cancelPlanSwitchSuccess)
        onCancelPlanSwitchSuccess?.()
      },
      onError: (error) => {
        showError(error.response?.data?.error || messages.cancelPlanSwitchError)
        captureException(error, { tags: { context: 'cancel_plan_switch' } })
      },
    })
  }, [deleteData, messages, showSuccess, showError, captureException, onCancelPlanSwitchSuccess])

  return {
    isCancelModalOpen,
    isLoading,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleCancelSubscription,
    // Both call restore_subscription — backend determines action based on subscription state
    handleRenewSubscription: handleRestoreSubscription,
    handleReactivateSubscription: handleRestoreSubscription,
    handleRetryPayment,
    retryState,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    handlePauseSubscription,
    isPauseLoading,
    handleResumeSubscription,
    isResumeLoading,
  }
}

export default useSubscription
